import { get, isNil } from 'lodash';
import { type FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { toBoolean } from 'src/common/utils/boolean';
import { useCoverSheetCardContext } from 'src/views/Coversheet/contexts/CoverSheetCardContext';
import { indexifyValues } from 'src/views/Coversheet/utils/indexifyValues';
import type { Card, Field } from '../configurations/types';

const isLookingForBoolean = (hasValue: string) =>
  ['true', 'false'].includes(hasValue);

interface IsFieldVisibleOptions {
  card: Card;
  index: number;
  showWhen: Field['showWhen'];
  fieldValues: FieldValues;
  visibleForIndex?: number | null;
}

export const isFieldVisible = ({
  card,
  index,
  showWhen,
  fieldValues,
  visibleForIndex,
}: IsFieldVisibleOptions) => {
  // Visible for index takes precedent over showWhen constraint
  if (!isNil(visibleForIndex) && index !== visibleForIndex) {
    return false;
  }

  if (!showWhen) {
    return true;
  }

  const showWhenValue = get(fieldValues, showWhen.fieldName) as unknown;
  const showThisField =
    `${
      isLookingForBoolean(showWhen.hasValue)
        ? toBoolean(showWhenValue)
        : showWhenValue
    }` === showWhen.hasValue;

  if (!showThisField) {
    return false;
  }

  /**
   * Support one level of showWhen chaining
   * e.g. Does the field that this field bases it's visibility on also have a
   * showWhen constraint? If so also respect that constraint, but no further.
   * (Do not see a need to support infinite chaining)
   */
  const parentField = indexifyValues<Card>(card, index)?.fields?.find(
    (f) => f.name === showWhen.fieldName,
  );

  if (!parentField?.showWhen) {
    return true;
  }

  const showWhenParentValue = get(
    fieldValues,
    parentField.showWhen.fieldName,
  ) as unknown;
  const showParentField =
    `${
      isLookingForBoolean(parentField.showWhen.hasValue)
        ? toBoolean(showWhenParentValue)
        : showWhenParentValue
    }` === parentField.showWhen.hasValue;

  return showParentField;
};

export const useIsFieldVisible = (
  showWhen: Field['showWhen'],
  visibleForIndex?: number | null,
) => {
  const { card, index } = useCoverSheetCardContext();
  const { getValues } = useFormContext();

  const parentField = indexifyValues<Card>(card, index)?.fields?.find(
    (f) => f.name === showWhen?.fieldName,
  );

  const fieldsToWatch = [
    showWhen?.fieldName,
    parentField?.showWhen?.fieldName,
  ].filter((f) => f !== undefined);

  useWatch({ name: fieldsToWatch });

  return isFieldVisible({
    card,
    index,
    showWhen,
    fieldValues: getValues(),
    visibleForIndex,
  });
};
